<template>
  <div class="app-error" v-if="this.isInError">
    <i class="material-icons">error_outline</i>
    <div>{{$t('errorMissingDeviceHash')}}</div>
  </div>
  <div v-else>
    <div class="app-loading" v-if="this.isLoading">
      <i class="material-icons">sync</i>
    </div>
    <div v-if="this.isInLogin">
      <HeaderRibbon v-bind:contractName="contractName" v-bind:locationName="locationName" v-bind:logoUri="logoUri" />
      <Login ref="loginElt" v-on:code-set="onCodeSet($event)" />
    </div>
    <div v-if="this.hasIndicators">
      <HeaderRibbon v-bind:contractName="contractName" v-bind:locationName="locationName" v-bind:logoUri="logoUri" />
      <SummaryMode
        v-if="!detailedModeIndicatorItem"
        v-bind:contractType="contractType"
        v-bind:indicatorItems="indicatorItems"
        v-bind:hasPurifier="hasPurifier"
        v-bind:purifierFanSpeed="purifierFanSpeed"
        v-on:select-indicator="onSelectIndicator"
      />
      <DetailedMode
        v-else
        v-bind:indicatorItems="indicatorItems"
        v-bind:hasPurifier="hasPurifier"
        v-bind:initialIndicatorItem="detailedModeIndicatorItem"
        v-bind:indicatorContainerId="indicatorContainerId"
      />
    </div>
  </div>
</template>

<script>
import Login from './components/Login.vue'
import HeaderRibbon from './components/HeaderRibbon.vue'
import SummaryMode from './components/SummaryMode.vue'
import DetailedMode from './components/DetailedMode.vue'
import { ExtendIndicatorItemList } from './IndicatorItems'

const _indicatorDisplayOrder = new Map([
  ['TEMP', 1],
  ['HUM', 2],
  ['CO2', 3],
  ['VOC', 4],
  ['PART', 5]
])

export default {
  name: 'App',
  props: ['deviceHash'],
  data: () => ({
    deviceCode: 0,
    indicatorItems: null,
    detailedModeIndicatorItem: null,
    indicatorContainerId: null,
    contractName: '',
    locationName: '',
    logoUri: '',
    contractType: null,
    hasPurifier: false,
    purifierFanSpeed: null,
    deviceData: null,
    deviceStatusTitle: null,
    deviceDataResources: null,
    isInLogin: false,
    isInError: false
  }),
  computed: {
    isLoading() {
      !this.isInError && !this.hasIndicators && !this.isInLogin
    },
    hasIndicators() {
      return !this.isInError && this.indicatorItems
    }
  },
  components: { Login, HeaderRibbon, SummaryMode, DetailedMode },
  mounted() {
    this.onMounted()
  },
  methods: {
    async onMounted() {
      if (!this.deviceHash || this.deviceHash.length === 0) {
        this.isInError = true
        return
      }

      window.addEventListener('popstate', evt => {
        // Very easy routing management :-)
        if (evt.state && evt.state.type === 'StateDetails') {
          const item = this.indicatorItems.find(
            x => x.data.DataType === evt.state.payload
          )
          this.detailedModeIndicatorItem = item
        } else {
          this.detailedModeIndicatorItem = null
        }
      })

      const basicDetails = await this.$fetch(
        `contract/${this.deviceHash}/basicdetails`
      )

      if (!basicDetails) {
        // No device found for the given hash
        this.isInError = true
        return
      }

      this.contractName = basicDetails.ContractName
      this.locationName = basicDetails.LocationName
      this.logoUri = basicDetails.LogoUri

      if (!basicDetails.IsPinCodeDisabled) {
        this.isInLogin = true
      } else {
        await this.loadIndicators()
      }
    },

    async onCodeSet(code) {
      this.deviceCode = code
      await this.loadIndicators()
    },

    onSelectIndicator(indicatorItem) {
      window.history.pushState(
        {
          type: 'StateDetails',
          payload: indicatorItem.data.DataType
        },
        'Azimut',
        '#details'
      )
      this.detailedModeIndicatorItem = indicatorItem
    },

    async loadIndicators() {
      const contractInfo = await this.$fetch(
        `contract/${this.deviceHash}?code=${this.deviceCode}`
      )

      if (!contractInfo) {
        // The input device code is incorrect
        this.$refs.loginElt.resetCode()
        return
      }

      this.isInLogin = false

      this.indicatorContainerId = contractInfo.IdIndicatorContainer
      this.contractType = contractInfo.ContractType
      this.hasPurifier = contractInfo.HasPurifier

      const deviceData = await this.$fetch(
        `devicedata/${this.indicatorContainerId}`
      )

      if (!deviceData) {
        // Specific case when there is absolutly no available data for a valid device
        this.indicatorItems = []
        return
      }

      if (this.hasPurifier) {
        const purifierData = deviceData.find(
          data => data.DataType === 'PurifierFanSpeed'
        )
        if (purifierData) {
          this.purifierFanSpeed = purifierData.Value
        }
      }

      const indicatorItems = deviceData
        .filter(data => data.DataType !== 'PurifierFanSpeed') // The purifier fan speed is a 'special' value
        .map(data => ({
          data: data,
          resource: null
        }))
        .sort((i1, i2) =>
          _indicatorDisplayOrder.get(i1.data.DataType) <
          _indicatorDisplayOrder.get(i2.data.DataType)
            ? -1
            : 1
        )

      const resources = new Map()

      for (let item of indicatorItems) {
        item.resource = await this.$fetch(
          `indicatormd/${this.contractType}/${item.data.DataType}?language=${this.$languageId}`
        )
      }

      ExtendIndicatorItemList(indicatorItems)

      this.indicatorItems = indicatorItems
    }
  }
}
</script>

<style lang="less">
@import './app.less';

.app-error {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;

  > i {
    font-size: 5rem;
  }

  > div {
    margin-top: 0.5rem;
  }
}

.app-loading {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  > i {
    animation: 3s linear infinite infinite-loop;
    font-size: 5rem;
  }
}
</style>


// WEBPACK FOOTER //
// src/App.vue